/* eslint-disable @typescript-eslint/no-explicit-any */
import { Spinner } from '@heroui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { type ReactNode, useMemo, useState } from 'react';
import {
  LuFileText,
  LuMessageSquare,
  LuThumbsUp,
  LuCalendarDays,
  LuZap,
  LuTrophy,
  LuX,
  LuThumbsDown,
  LuTimer,
  LuLayoutList,
  LuClock,
} from 'react-icons/lu';
import { useFetchMyJobs } from '../../../hook/useQueryData';
import { useAcceptJob, useDeclineJob } from '../../../hook/useMutationData';
import CustomChip from '../../components/CustomChip';
import CustomButton from '../../components/CustomButton';
import CustomCardWithHeader from '../../components/CustomCardWithHeader';
import CommonConfirmModal from '../../components/common/ui/CommonConfirmModal';
import RequestCard from '../../components/quote-flow/RequestCard';
import { ProviderChipState } from '../../components/quote-flow/ProviderChip';
import {
  getEngagementStatusChipColor,
  getEngagementStatusLabel,
  getJobDisplayStatus,
  ENGAGEMENT_STATUS,
} from '../../../utils/engagement';
import { formatCurrency } from '../../../utils/currency';
import {
  QuoteRequest,
  SiblingEffectiveState,
  dismissRequest,
  getDismissedRequestKeys,
  getSiblingEffectiveState,
  groupJobsIntoRequests,
  pickRepresentativeJob,
} from '../../../utils/quoteFlowRequests';
import { Job } from '../../../utils/type';
import { all_routes } from '../../../core/data/routes/all_routes';
import PageHeader from '../../components/common/ui/PageHeader';
import CustomPagination from '../../components/common/ui/CustomPagination';
import StatsCard from '../../components/common/ui/StatsCard';
import { useFeatureFlag } from '../../../config/featureFlags';
import { FEATURE_FLAGS } from '../../../config/featureFlags';
import type { JobStatus } from '../../../utils/type';

const PAGE_SIZE = 10;

type JobStatusFilter = 'all' | JobStatus;

/**
 * Enhanced status filters with distinctive icons and color scheme
 * Each status has a unique visual identity for better recognition
 */
const STATUS_FILTERS: {
  id: JobStatusFilter;
  title: string;
  icon: ReactNode;
  iconBgClassName: string;
  colorAccent: string;
}[] = [
  {
    id: 'all',
    title: 'Total',
    icon: <LuLayoutList className="w-full h-full" />,
    iconBgClassName: 'bg-slate-100 text-slate-600',
    colorAccent: 'slate',
  },
  {
    id: 'Requested',
    title: 'Requested',
    icon: <LuFileText className="w-full h-full" />,
    iconBgClassName: 'bg-amber-100 text-amber-600',
    colorAccent: 'amber',
  },
  {
    id: 'Quoted',
    title: 'Quoted',
    icon: <LuMessageSquare className="w-full h-full" />,
    iconBgClassName: 'bg-[#0095B7] text-[#0095B7]',
    colorAccent: 'blue',
  },
  {
    id: 'Accepted',
    title: 'Accepted',
    icon: <LuThumbsUp className="w-full h-full" />,
    iconBgClassName: 'bg-green-100 text-green-600',
    colorAccent: 'green',
  },
  {
    id: 'Scheduled',
    title: 'Scheduled',
    icon: <LuCalendarDays className="w-full h-full" />,
    iconBgClassName: 'bg-purple-100 text-purple-600',
    colorAccent: 'purple',
  },
  {
    id: 'InProgress',
    title: 'In Progress',
    icon: <LuZap className="w-full h-full" />,
    iconBgClassName: 'bg-orange-100 text-orange-600',
    colorAccent: 'orange',
  },
  {
    id: 'Awaiting',
    title: 'Awaiting Confirmation',
    icon: <LuClock className="w-full h-full" />,
    iconBgClassName: 'bg-emerald-100 text-emerald-600',
    colorAccent: 'emerald',
  },
  {
    id: 'Completed',
    title: 'Completed',
    icon: <LuTrophy className="w-full h-full" />,
    iconBgClassName: 'bg-emerald-100 text-emerald-600',
    colorAccent: 'emerald',
  },
  {
    id: 'Cancelled',
    title: 'Cancelled',
    icon: <LuX className="w-full h-full" />,
    iconBgClassName: 'bg-red-100 text-red-600',
    colorAccent: 'red',
  },
  {
    id: 'Declined',
    title: 'Declined',
    icon: <LuThumbsDown className="w-full h-full" />,
    iconBgClassName: 'bg-rose-100 text-rose-600',
    colorAccent: 'rose',
  },
  {
    id: 'Expired',
    title: 'Expired',
    icon: <LuTimer className="w-full h-full" />,
    iconBgClassName: 'bg-gray-100 text-gray-500',
    colorAccent: 'gray',
  },
];

const formatDate = (value?: string): string => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Phase 2 — My Job Requests (customer list). Legacy per-job table view.
 * Kept unchanged as the flag-off fallback for `MyJobRequests` (below) — see
 * that component for the S6 redesign, gated behind `quote-broadcast`.
 * Route: /customer-job-requests
 */
const LegacyMyJobRequests = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [activeFilter, setActiveFilter] = useState<JobStatusFilter>('all');
  const { data, isLoading, isError, refetch } = useFetchMyJobs({
    page: currentPage,
    limit: PAGE_SIZE,
    status: activeFilter === 'all' ? undefined : activeFilter,
  });
  const jobs = data?.jobs ?? [];
  const totalPages = data?.pages ?? 1;
  const acceptMutation = useAcceptJob();
  const declineMutation = useDeclineJob();

  const handleFilterSelect = (filter: JobStatusFilter) => {
    setActiveFilter(filter);
    setCurrentPage(1);
  };

  const goToDetail = (job: Job) => {
    navigate(
      all_routes.customerJob.replace(':jobId', job.displayId || job.jobId)
    );
  };

  // Render individual job request as a table row.
  const renderJobRow = (job: Job) => {
    const serviceName =
      job.serviceTitle ||
      job.serviceName ||
      job.service?.title ||
      'Job Request';
    const providerName =
      job.businessName || job.provider?.name || job.providerName || '—';
    const isProviderInitiated = job.initiatedBy === 'provider';
    const canAcceptDecline =
      isProviderInitiated && job.status === ENGAGEMENT_STATUS.QUOTED;

    return (
      <tr
        key={job.jobId}
        onClick={() => goToDetail(job)}
        className="bg-white border-b hover:bg-secondary/10 text-left cursor-pointer"
      >
        <td className="px-6 py-4 font-medium">
          <div className="font-semibold">{serviceName}</div>
          <div className="text-gray-500 text-xs">
            {job.referenceCode || job.jobId}
          </div>
        </td>
        <td className="px-6 py-4">
          <div>{providerName}</div>
          {isProviderInitiated && (
            <CustomChip
              label={t(
                'jobs.provider_initiated',
                'Provider reached out to you'
              )}
              color="secondary"
              variant="flat"
              size="sm"
              className="mt-1"
            />
          )}
        </td>
        <td className="px-6 py-4">{formatDate(job.createdAt)}</td>
        <td className="px-6 py-4">
          <CustomChip
            label={getEngagementStatusLabel(getJobDisplayStatus(job))}
            color={
              getEngagementStatusChipColor(getJobDisplayStatus(job)) as any
            }
            variant="flat"
            size="sm"
          />
        </td>
        <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
          {canAcceptDecline ? (
            <div className="flex flex-col gap-2">
              <CommonConfirmModal
                buttonLabel={t('jobs.accept_quote', 'Accept Quote')}
                buttonColor="primary"
                buttonVariant="flat"
                title={t('jobs.confirm_accept_title', 'Accept this quote?')}
                description={t(
                  'jobs.confirm_accept_desc',
                  'Accepting locks in the quoted price. The provider will schedule the work.'
                )}
                confirmBtnText={t('jobs.accept_quote', 'Accept Quote')}
                confirmBtnColor="primary"
                cancelBtnText={t('jobs.cancel', 'Cancel')}
                isLoading={acceptMutation.isPending}
                onConfirm={() => acceptMutation.mutate(job.jobId)}
                triggerClassName="w-full"
              />
              <CommonConfirmModal
                buttonLabel={t('jobs.decline_quote', 'Decline Quote')}
                buttonColor="danger"
                buttonVariant="flat"
                title={t('jobs.confirm_decline_title', 'Decline this quote?')}
                description={t(
                  'jobs.confirm_decline_desc',
                  'Declining closes this request. You can request a new quote if you change your mind.'
                )}
                confirmBtnText={t('jobs.decline_quote', 'Decline Quote')}
                confirmBtnColor="danger"
                cancelBtnText={t('jobs.cancel', 'Cancel')}
                requireReason
                reasonLabel={t(
                  'jobs.decline_reason_label',
                  'Reason for declining (optional)'
                )}
                reasonDescription={t(
                  'jobs.decline_reason_desc',
                  "Let the provider know why, if you'd like - this step is optional."
                )}
                isLoading={declineMutation.isPending}
                onConfirm={(reason) =>
                  declineMutation.mutate({
                    jobId: job.jobId,
                    declinedNote: reason,
                  })
                }
                triggerClassName="w-full"
              />
              <CustomButton
                label={t('jobs.view_details', 'View Details')}
                color="primary"
                variant="flat"
                size="sm"
                fullWidth
                onPress={() => goToDetail(job)}
                className="text-white bg-primary"
              />
            </div>
          ) : (
            <CustomButton
              label={t('jobs.view_details', 'View Details')}
              color="primary"
              variant="flat"
              size="sm"
              onPress={() => goToDetail(job)}
              className="text-white bg-primary"
            />
          )}
        </td>
      </tr>
    );
  };

  return (
    <div>
      <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
        <div>
          <PageHeader
            title={t('jobs.my_requests', 'My Job Requests')}
            description={t(
              'jobs.my_requests_subtitle',
              'Track your requests and review provider quotes.'
            )}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 tablet:grid-cols-3 laptop:grid-cols-5 gap-2.5 md:gap-3 mb-6">
        {STATUS_FILTERS.map((filter) => (
          <StatsCard
            key={filter.id}
            size="md"
            variant="filter"
            filterId={filter.id}
            isActive={activeFilter === filter.id}
            title={filter.title}
            value={
              filter.id === 'all'
                ? jobs.length
                : jobs.filter((job) => job.status === filter.id).length
            }
            icon={filter.icon}
            iconBgClassName={filter.iconBgClassName}
            onClick={() => handleFilterSelect(filter.id)}
          />
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <Spinner
            label={t('jobs.loading', 'Loading your requests…')}
            color="primary"
          />
        </div>
      ) : isError ? (
        <CustomCardWithHeader
          title={t('jobs.error_title', 'Unable to Load Requests')}
          subtitle={t(
            'jobs.load_error',
            'We could not load your job requests.'
          )}
          mainContent={
            <div className="flex justify-center py-6">
              <CustomButton
                label={t('jobs.retry', 'Retry')}
                color="primary"
                variant="flat"
                onPress={() => refetch()}
              />
            </div>
          }
          className="border-red-200 bg-red-50/50"
        />
      ) : jobs.length === 0 ? (
        <CustomCardWithHeader
          title={
            activeFilter === 'all'
              ? t('jobs.empty_title', 'No Job Requests Yet')
              : t('jobs.empty_filtered_title', 'No Matching Job Requests')
          }
          subtitle={t(
            activeFilter === 'all'
              ? 'jobs.empty_hint'
              : 'jobs.empty_filtered_hint',
            activeFilter === 'all'
              ? 'When you request a quote from a service, it will appear here.'
              : 'There are no job requests with this status.'
          )}
          mainContent={
            <div className="flex flex-col items-center gap-4 py-8 text-center">
              <LuFileText size={48} className="text-gray-300" />
              <p className="text-sm text-gray-500 max-w-sm">
                {activeFilter === 'all'
                  ? t(
                      'jobs.empty_message',
                      'Start by requesting a quote to see your jobs here.'
                    )
                  : t(
                      'jobs.empty_filtered_message',
                      'Choose another status to view more job requests.'
                    )}
              </p>
            </div>
          }
          className="bg-gray-50/50"
        />
      ) : (
        <>
          <div className="border border-gray-200 rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-body text-left text-gray-600">
                <thead className="bg-secondary/20 text-body-bold">
                  <tr>
                    <th scope="col" className="px-6 py-3">
                      {t('jobs.col_service', 'Service')}
                    </th>
                    <th scope="col" className="px-6 py-3">
                      {t('jobs.col_provider', 'Provider')}
                    </th>
                    <th scope="col" className="px-6 py-3">
                      {t('jobs.col_submitted', 'Submitted')}
                    </th>
                    <th scope="col" className="px-6 py-3">
                      {t('jobs.col_status', 'Status')}
                    </th>
                    <th scope="col" className="px-6 py-3">
                      {t('jobs.col_action', 'Action')}
                    </th>
                  </tr>
                </thead>
                <tbody>{jobs.map((job) => renderJobRow(job))}</tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-end items-center py-6 mt-4">
              <CustomPagination
                page={currentPage}
                initialPage={1}
                total={totalPages}
                size="md"
                onChange={setCurrentPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ── S6 — redesigned "My Requests" (quote-broadcast flag) ──────────────────

const EFFECTIVE_TO_CHIP_STATE: Record<
  SiblingEffectiveState,
  ProviderChipState
> = {
  booked: 'booked',
  cancelled: 'declined',
  declined: 'declined',
  'quoted-expired': 'quoted',
  'quoted-live': 'quoted',
  requested: 'not-yet',
};

type DynamicTab = 'active' | JobStatus;

const STATUS_TABS: { id: DynamicTab; label: string }[] = [
  { id: 'active', label: 'Active' },
  { id: 'Requested', label: 'Requested' },
  { id: 'Quoted', label: 'Quoted' },
  { id: 'Accepted', label: 'Accepted' },
  { id: 'Scheduled', label: 'Scheduled' },
  { id: 'InProgress', label: 'In Progress' },
  { id: 'Awaiting', label: 'Awaiting' },
  { id: 'Completed', label: 'Completed' },
  { id: 'Cancelled', label: 'Cancelled' },
  { id: 'Declined', label: 'Declined' },
  { id: 'Expired', label: 'Expired' },
];

const S6_FETCH_LIMIT = 100;

/** Maps job statuses to visually accurate status labels and colors */
const getJobCardStatusProps = (
  job: Job
): {
  statusLabel: string;
  statusColor: 'default' | 'primary' | 'success' | 'warning' | 'danger';
} => {
  const displayStatus = getJobDisplayStatus(job);
  switch (displayStatus) {
    case 'Requested':
      return { statusLabel: 'Requested', statusColor: 'warning' };
    case 'Quoted':
      return { statusLabel: 'Quoted', statusColor: 'primary' };
    case 'Accepted':
      return { statusLabel: 'Accepted', statusColor: 'success' };
    case 'Scheduled':
      return { statusLabel: 'Scheduled', statusColor: 'primary' };
    case 'InProgress':
      return { statusLabel: 'In Progress', statusColor: 'warning' };
    case 'Awaiting':
      return { statusLabel: 'Awaiting Confirmation', statusColor: 'success' };
    case 'Completed':
      return { statusLabel: 'Completed', statusColor: 'success' };
    case 'Cancelled':
      return { statusLabel: 'Cancelled', statusColor: 'danger' };
    case 'Declined':
      return { statusLabel: 'Declined', statusColor: 'danger' };
    case 'Expired':
      return { statusLabel: 'Expired', statusColor: 'default' };
    default:
      return {
        statusLabel: displayStatus || 'Requested',
        statusColor: 'default',
      };
  }
};

const QuoteFlowMyRequests = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<DynamicTab>('active');
  const [dismissedVersion, setDismissedVersion] = useState(0);
  const { data, isLoading, isError, refetch } = useFetchMyJobs({
    limit: S6_FETCH_LIMIT,
  });

  const requests = useMemo(() => {
    const rawJobs = data?.jobs ?? [];
    return groupJobsIntoRequests(rawJobs, {
      dismissedKeys: getDismissedRequestKeys(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dismissedVersion]);

  // Calculate total counts for 'Active' as well as specific statuses
  const counts = useMemo(() => {
    const countsMap: Record<string, number> = {
      active: requests.filter((r) => {
        if (r.tab !== 'active') return false;
        // Exclude requests whose representative job resolves to Expired
        const rep = pickRepresentativeJob(r);
        return getJobDisplayStatus(rep) !== 'Expired';
      }).length,
    };

    STATUS_TABS.forEach((tab) => {
      if (tab.id !== 'active') {
        countsMap[tab.id] = requests.filter((r) => {
          const rep = pickRepresentativeJob(r);
          return rep.status === tab.id;
        }).length;
      }
    });

    return countsMap;
  }, [requests]);

  // Filter visible requests depending on whether 'active' or a specific status tab is selected
  const visible = useMemo(() => {
    if (activeTab === 'active') {
      return requests.filter((r) => {
        if (r.tab !== 'active') return false;
        // Exclude requests whose representative job resolves to Expired
        const rep = pickRepresentativeJob(r);
        return getJobDisplayStatus(rep) !== 'Expired';
      });
    }
    return requests.filter((r) => {
      const rep = pickRepresentativeJob(r);
      return rep.status === activeTab;
    });
  }, [requests, activeTab]);

  const needsYou = visible.filter((r) => r.group === 'needs-you');
  const waiting = visible.filter((r) => r.group === 'waiting-on-providers');

  const goToJob = (job: Job) =>
    navigate(
      all_routes.customerJob.replace(':jobId', job.displayId || job.jobId)
    );

  const handleDismiss = (key: string) => {
    dismissRequest(key);
    setDismissedVersion((v) => v + 1);
  };

  const leadJob = (request: QuoteRequest): Job => request.jobs[0];

  const buildCardProps = (request: QuoteRequest) => {
    const lead = leadJob(request);
    const isBroadcast = request.jobs.length > 1;
    const rep = pickRepresentativeJob(request);

    // Check if a quote in the broadcast request has been accepted/booked
    const isAccepted =
      rep.status === 'Accepted' ||
      rep.status === 'Scheduled' ||
      rep.status === 'InProgress' ||
      rep.status === 'Completed';

    const rawServiceTitle =
      rep.serviceTitle ||
      rep.serviceName ||
      rep.service?.title ||
      lead.serviceTitle ||
      lead.serviceName ||
      lead.service?.title ||
      'Job Request';

    // If accepted, show the winning service title directly. Otherwise, show user-friendly broadcast title.
    const titleText = isBroadcast
      ? isAccepted
        ? rawServiceTitle
        : 'Multi-Provider Quote Request'
      : rawServiceTitle;

    // Render title element with inline 'Broadcast' chip when applicable
    const title = (
      <div className="flex items-center gap-2 flex-wrap">
        <span>{titleText}</span>
        {isBroadcast && (
          <CustomChip
            label="Broadcast"
            color="primary"
            variant="flat"
            size="sm"
            className="font-bold text-[11px] bg-white test-primary border-1 border-primary"
          />
        )}
      </div>
    );

    const sentDate = request.jobs.reduce(
      (earliest, j) =>
        (j.createdAt ?? '') < (earliest ?? '') ? j.createdAt : earliest,
      lead.createdAt
    );
    const metaLine = isBroadcast
      ? `${lead.referenceCode || lead.displayId || lead.jobId} · sent to ${request.jobs.length} pros ${formatDate(sentDate)}`
      : `${lead.referenceCode || lead.displayId || lead.jobId} · sent ${formatDate(sentDate)}`;

    const providers = request.jobs.map((j) => ({
      name: j.businessName || j.providerName || j.provider?.name || 'Provider',
      state: EFFECTIVE_TO_CHIP_STATE[getSiblingEffectiveState(j)],
      price: typeof j.totalPrice === 'number' ? j.totalPrice : undefined,
    }));

    const showProviderInitiatedChip = request.jobs.some(
      (j) => j.initiatedBy === 'provider'
    );

    return {
      lead,
      isBroadcast,
      title,
      metaLine,
      providers,
      showProviderInitiatedChip,
    };
  };

  const renderCard = (request: QuoteRequest) => {
    const { title, metaLine, providers, showProviderInitiatedChip } =
      buildCardProps(request);
    const target = pickRepresentativeJob(request);
    const { statusLabel, statusColor } = getJobCardStatusProps(target);

    // Active tab section handling
    if (activeTab === 'active') {
      if (request.allDeclined) {
        const n = request.jobs.length;
        return (
          <RequestCard
            key={request.key}
            title={title}
            statusLabel="Waiting"
            statusColor="warning"
            showProviderInitiatedChip={showProviderInitiatedChip}
            metaLine={metaLine}
            providers={providers}
            prowlText={
              n > 1
                ? `All ${n} pros passed on this one. Want me to find more nearby?`
                : `${providers[0]?.name || 'The provider'} passed on this one. Want me to find more nearby?`
            }
            actionLabel="Show me"
            actionVariant="solid"
            onAction={() => goToJob(target)}
            dismissLabel="Dismiss"
            onDismiss={() => handleDismiss(request.key)}
          />
        );
      }

      if (request.group === 'needs-you') {
        const n = request.quotesInCount;
        const quotedPrices = request.jobs
          .filter((j) => getSiblingEffectiveState(j) === 'quoted-live')
          .map((j) => j.quotedPrice)
          .filter((p): p is number => typeof p === 'number');
        const spread =
          quotedPrices.length >= 2
            ? Math.max(...quotedPrices) - Math.min(...quotedPrices)
            : undefined;

        return (
          <RequestCard
            key={request.key}
            title={title}
            statusLabel={statusLabel}
            statusColor={statusColor}
            showProviderInitiatedChip={showProviderInitiatedChip}
            metaLine={metaLine}
            providers={providers}
            prowlText={
              n > 1
                ? `${n} quotes in${
                    spread !== undefined
                      ? `, ${formatCurrency(spread)} apart`
                      : ''
                  } — worth a look.`
                : undefined
            }
            actionLabel={n > 1 ? `View ${n} quotes` : 'View quote'}
            actionVariant="solid"
            onAction={() => goToJob(target)}
          />
        );
      }

      // Waiting on providers
      return (
        <RequestCard
          key={request.key}
          title={title}
          statusLabel={statusLabel}
          statusColor={statusColor}
          showProviderInitiatedChip={showProviderInitiatedChip}
          metaLine={metaLine}
          providers={providers}
          prowlText="First reply usually arrives within a few hours."
          actionLabel="View request"
          actionVariant="bordered"
          onAction={() => goToJob(target)}
        />
      );
    }

    // Default card view for all status tabs
    return (
      <RequestCard
        key={request.key}
        title={title}
        statusLabel={statusLabel}
        statusColor={statusColor}
        showProviderInitiatedChip={showProviderInitiatedChip}
        metaLine={metaLine}
        providers={providers}
        actionLabel="View details"
        actionVariant="solid"
        onAction={() => goToJob(target)}
      />
    );
  };

  return (
    <div>
      <div className="mb-6">
        <PageHeader
          title="My Requests"
          description="Track your requests and review provider quotes."
        />
      </div>

      {/* Single scrollable row displaying status tabs spanning full width with balanced spacing */}
      <div className="min-w-[110px] overflow-x-auto no-scrollbar mb-6">
        <div className="flex gap-1 bg-gray-100 rounded-[10px] p-1.5 min-w-[110px]">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 min-w-[90px] text-center px-3 py-1 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap shrink-0 ${
                activeTab === tab.id
                  ? 'bg-white text-primary border border-primary shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label} · {counts[tab.id] ?? 0}
            </button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <Spinner label="Loading your requests…" color="primary" />
        </div>
      ) : isError ? (
        <CustomCardWithHeader
          title="Unable to Load Requests"
          subtitle="We could not load your job requests."
          mainContent={
            <div className="flex justify-center py-6">
              <CustomButton
                label="Retry"
                color="primary"
                variant="flat"
                onPress={() => refetch()}
              />
            </div>
          }
          className="border-red-200 bg-red-50/50"
        />
      ) : visible.length === 0 ? (
        <CustomCardWithHeader
          title="Nothing here yet"
          subtitle={
            activeTab === 'active'
              ? 'When you request a quote, it will show up here.'
              : `No ${activeTab} requests found.`
          }
          mainContent={<div className="py-4" />}
          className="bg-gray-50/50"
        />
      ) : activeTab === 'active' ? (
        <div className="space-y-8">
          <div>
            <p className="text-xs font-medium text-gray-500 mb-3">
              Needs you
              {needsYou.length > 0 &&
                ` · ${needsYou.length} requests waiting on a decision`}
            </p>
            {needsYou.length > 0 ? (
              <div className="space-y-3">{needsYou.map(renderCard)}</div>
            ) : (
              <p className="text-sm text-gray-400">
                Nothing needs your attention right now.
              </p>
            )}
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-3">
              Waiting on providers
              {waiting.length === 0 && ' · nothing for you to do yet'}
            </p>
            {waiting.length > 0 ? (
              <div className="space-y-3">{waiting.map(renderCard)}</div>
            ) : (
              <p className="text-sm text-gray-400">
                Nothing waiting on providers right now.
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-3">{visible.map(renderCard)}</div>
      )}
    </div>
  );
};

/**
 * Phase 2 — My Job Requests (customer list).
 * Route: /customer-job-requests
 * Behind the `quote-broadcast` flag: S6 redesign (grouped request cards, dynamic
 * tabs) when on, the legacy per-job table when off — see README S6 /
 * plan.md for the design source and judgment calls.
 */
const MyJobRequests = () => {
  const quoteBroadcastEnabled = useFeatureFlag(FEATURE_FLAGS.quoteBroadcast);
  return quoteBroadcastEnabled ? (
    <QuoteFlowMyRequests />
  ) : (
    <LegacyMyJobRequests />
  );
};

export default MyJobRequests;
